import { colors } from "@indeed/ifl-tokens/react-native";
import React, {
  type RefObject,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { Platform, TextInput, type TextInputProps } from "react-native";

import { Text, Button, Alert, Flex, Pressable, type BoxRef } from "../../../mads";
import { ChevronDown, ChevronUp, Close, Hide } from "../../../mads/icons";
import { useI18n, useLocale, type GettextInterface } from "../../I18n";
import { serializeLocaleToBCP47 } from "../../I18n/Locale";
import { setUniversalFocus } from "../../util/accessibilityFocus";
import { announcePolite } from "../../util/announcePolite";
import { getLocaleCurrency } from "../../util/salary/getLocaleCurrency";
import { getPayTypesToLabels } from "../../util/salary/getPayTypesToLabels";
import { parseLocaleCurrencyAmount } from "../../util/salary/parseLocaleCurrencyAmount";
import { SalaryType } from "../../util/salary/SalaryType";
import { FormField, ChipFormField } from "./components";
import { styles } from "./styles";
import {
  validatePayRange,
  type SalaryPreference,
  type PayBoundsMap,
  filterPayInput,
  isValidPayInput,
  formatPayInputForDisplay,
} from "./validation";

export type { SalaryPreference, PayBoundsMap } from "./validation";

export interface MinimumPayProps {
  value?: SalaryPreference;
  onChange: (salaryPreference: SalaryPreference) => void;
  showErrors?: boolean;
  showEmployerAlert?: boolean;
  variant?: "default" | "compact";
  /** Per-pay-period bounds override (homepage's server-driven ranges); falls back to the per-country table. */
  payBounds?: PayBoundsMap;
  /** Whether an amount is required. Defaults to `true`; `false` allows an empty amount (e.g. skippable onboarding). */
  required?: boolean;
  /** Helper text rendered under the "Minimum base pay" label. */
  description?: string;
  /** Ref forwarded to the amount TextInput for focus management. */
  amountInputRef?: RefObject<TextInput | null>;
  /** Focus handler for the amount TextInput. */
  onAmountFocus?: TextInputProps["onFocus"];
  /** Ref forwarded to the first pay-period chip for focus order (WCAG). */
  payPeriodFirstChipRef?: RefObject<BoxRef | null>;
  /** Accessibility hint for the pay-period chip group. */
  payPeriodAccessibilityHint?: string;
  /** Test id for the amount TextInput. */
  amountInputTestID?: string;
  /** Test id prefix for the pay-period chips (`${prefix}-${salaryType}`). */
  payPeriodTestIdPrefix?: string;
  /** Disables the amount input and pay-period chips (e.g. during save/delete). */
  disabled?: boolean;
  /** `nativeID` for the amount input's iOS `inputAccessoryViewID`, letting a consumer render a keyboard toolbar. iOS only. */
  inputAccessoryViewID?: string;
}

// PayInput component for the currency input field
export const PayInput = forwardRef<
  TextInput,
  TextInputProps & {
    invalid: boolean;
    /** When set (and the field is editable with a value), shows a clear button with this label. */
    clearAccessibilityLabel?: string;
  }
>(
  (
    {
      invalid,
      clearAccessibilityLabel,
      value,
      onChangeText,
      editable,
      onFocus,
      onBlur,
      ...inputProps
    },
    ref,
  ) => {
    const localeObj = useLocale();
    const locale = serializeLocaleToBCP47(localeObj);
    const { hasRighthandSymbol, currencySymbol } = getLocaleCurrency(locale);
    // Presentational: the currency is conveyed to screen readers via the input's accessibilityHint,
    // so the glyph itself is hidden from assistive tech (otherwise it's read as stray text).
    const currencyElement = (
      <Text sx={styles.payInputCurrency} accessible={false} ariaHidden>
        {currencySymbol}
      </Text>
    );
    // Track focus so the wrapper (not the inner input) renders the web focus ring, wrapping the whole
    // field — currency glyph + input + clear button — instead of just the middle input.
    const [isFocused, setIsFocused] = useState(false);
    const wrapperStyles = {
      ...styles.payInputWrapper,
      ...(invalid ? styles.payInputWrapperInvalid : {}),
      ...(isFocused ? styles.payInputWrapperFocused : {}),
    };

    // Own the TextInput ref internally so the clear button can restore focus after clearing, while
    // still forwarding it for external focus management.
    const innerRef = useRef<TextInput | null>(null);
    const setInputRef = useCallback(
      (node: TextInput | null) => {
        innerRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    const showClearButton =
      editable !== false && !!clearAccessibilityLabel && !!onChangeText && !!value;

    return (
      <Flex sx={wrapperStyles}>
        {!hasRighthandSymbol && currencyElement}
        <TextInput
          ref={setInputRef}
          style={styles.payInput}
          inputMode="decimal"
          value={value}
          onChangeText={onChangeText}
          editable={editable}
          onFocus={(e) => {
            setIsFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            onBlur?.(e);
          }}
          {...inputProps}
        />
        {hasRighthandSymbol && currencyElement}
        {showClearButton && (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={clearAccessibilityLabel}
            sx={styles.payInputClearButton}
            onPress={() => {
              onChangeText?.("");
              innerRef.current?.focus();
            }}
          >
            <Close size="sm" aria-hidden />
          </Pressable>
        )}
      </Flex>
    );
  },
);

PayInput.displayName = "PayInput";

// EmployersPayInfoAlert component
export const EmployersPayInfoAlert = () => {
  const gt = useI18n();

  return (
    <Alert
      icon={<Hide color={colors.foregroundStatusInfo} aria-hidden />}
      status="info"
      explainerText={gt.gettext("Employers won't see this preference.")}
    />
  );
};

const INITIAL_SHOW_COUNT = 2;

// PayPeriodSelect component for selecting the pay period
export const PayPeriodSelect = ({
  invalid,
  value,
  onChange,
  errorText,
  showRequiredIndicator = false,
  firstChipRef,
  accessibilityHint,
  testIdPrefix,
  disabled,
}: {
  invalid?: boolean;
  value?: SalaryPreference;
  onChange: (salaryPreference: SalaryPreference) => void;
  errorText?: string;
  showRequiredIndicator?: boolean;
  firstChipRef?: RefObject<BoxRef | null>;
  accessibilityHint?: string;
  testIdPrefix?: string;
  disabled?: boolean;
}) => {
  const PAY_OPTIONS_ORDER = useMemo(
    () => [
      SalaryType.Hourly,
      SalaryType.Yearly,
      SalaryType.Monthly,
      SalaryType.Weekly,
      SalaryType.Daily,
    ],
    [],
  );

  const gt = useI18n();
  const amount = value?.amount ?? "";
  const salaryType = value?.salaryType ?? "";

  const payPeriodLabel = gt.pgettext("Pay period form field label", "Pay period");
  const payTypesToLabels = getPayTypesToLabels(gt);

  // The pay type is pre-selected based on the job's pay type.
  // If the selected pay type is below the fold, show all on load.
  const shouldBeExpandedOnInitialLoad =
    PAY_OPTIONS_ORDER.slice(INITIAL_SHOW_COUNT).filter(
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      (payType) => payType === (salaryType as SalaryType),
    ).length > 0;

  const [isExpanded, setIsExpanded] = useState(shouldBeExpandedOnInitialLoad);

  const firstNewChipRef = useRef<BoxRef>(null);
  const userExpandedRef = useRef(false);

  // Move native screen-reader focus to the revealed chips. The delay lets the chips mount first.
  useEffect(() => {
    if (isExpanded && userExpandedRef.current) {
      userExpandedRef.current = false;
      const timer = setTimeout(
        () => setUniversalFocus(firstNewChipRef, { keyboardFocus: true }),
        100,
      );
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  const selectSalaryType = useCallback(
    (selected: string) => onChange({ amount, salaryType: selected, sentiment: value?.sentiment }),
    [amount, onChange, value?.sentiment],
  );

  const items = useMemo(
    () =>
      PAY_OPTIONS_ORDER.slice(0, isExpanded ? undefined : INITIAL_SHOW_COUNT).map(
        (payType, index) => ({
          value: payType,
          label: `${payTypesToLabels[payType][0].toUpperCase()}${payTypesToLabels[payType].slice(1)}`,
          buttonRef: index === INITIAL_SHOW_COUNT ? firstNewChipRef : undefined,
        }),
      ),
    [isExpanded, payTypesToLabels, PAY_OPTIONS_ORDER],
  );

  return (
    <Flex sx={styles.inputBox}>
      <ChipFormField
        firstChipRef={firstChipRef}
        accessibilityHint={accessibilityHint}
        testIdPrefix={testIdPrefix}
        disabled={disabled}
        label={payPeriodLabel}
        invalid={!!invalid}
        size="sm"
        errorText={errorText}
        showRequiredIndicator={showRequiredIndicator}
        items={items}
        checkedValue={salaryType}
        onChange={selectSalaryType}
      />
      <Button
        variant="text"
        disabled={disabled}
        onPress={() => {
          const willExpand = !isExpanded;
          if (!willExpand) {
            announcePolite(gt.gettext("Fewer pay period options shown"));
          } else if (Platform.OS === "web") {
            // Web keeps focus on this button, so announce the reveal; native moves focus instead.
            announcePolite(gt.gettext("More pay period options shown"));
          } else {
            userExpandedRef.current = true;
          }
          setIsExpanded(willExpand);
        }}
        iconAfter={isExpanded ? <ChevronUp aria-hidden /> : <ChevronDown aria-hidden />}
        sx={{ paddingLeft: 0, paddingRight: 0 }}
      >
        {isExpanded ? gt.gettext("Show less pay periods") : gt.gettext("Show more pay periods")}
      </Button>
    </Flex>
  );
};

// Helper function to get error messages
export function getPayErrorMessages({
  gt,
  value,
  showErrors,
  locale,
  payBounds,
  required = true,
}: {
  value?: SalaryPreference;
  gt: GettextInterface;
  showErrors?: boolean;
  locale: string;
  /** Per-pay-period bounds override (homepage's server-driven ranges). */
  payBounds?: PayBoundsMap;
  /** Whether an amount is required. Defaults to `true`; when `false` an empty amount is allowed (a zero still errors). */
  required?: boolean;
}) {
  // Reject a non-empty amount that doesn't parse to a finite number (a lone "." or ","); otherwise
  // homepage would persist it as 0. Treated like a zero.
  const parsedAmount = value?.amount ? parseLocaleCurrencyAmount(value.amount, locale) : Number.NaN;
  const isAmountInvalidOrZero =
    !!value?.amount && (!Number.isFinite(parsedAmount) || parsedAmount === 0);
  const payRangeValidationError =
    (value && validatePayRange(value, locale, gt, payBounds)) || undefined;

  const shouldShowRequiredError = !value?.amount && required;

  return {
    // Gate the *displayed* range error on showErrors, like inputError/selectError, so an
    // out-of-range amount doesn't render an error on load or mid-typing (submit-only display).
    // Submit gating is unaffected: callers that gate on save pass showErrors: true.
    fieldsetError: showErrors ? payRangeValidationError : undefined,
    inputError:
      showErrors && (isAmountInvalidOrZero || shouldShowRequiredError) && !payRangeValidationError
        ? isAmountInvalidOrZero
          ? gt.pgettext(
              "Minimum pay greater than zero error text",
              "Enter an amount greater than 0",
            )
          : gt.pgettext("Minimum pay required error text", "Minimum pay is required")
        : "",
    selectError:
      // When not required, only demand a period once an amount is entered — a fully empty field is a valid skip.
      showErrors && !value?.salaryType && !payRangeValidationError && (required || !!value?.amount)
        ? gt.pgettext("Pay period error text", "Pay period is required")
        : "",
  };
}

// Main MinimumPay component
export const MinimumPay: React.FC<MinimumPayProps> = ({
  value,
  onChange,
  showErrors,
  showEmployerAlert = true,
  variant = "default",
  payBounds,
  required = true,
  description,
  amountInputRef,
  onAmountFocus,
  payPeriodFirstChipRef,
  payPeriodAccessibilityHint,
  amountInputTestID,
  payPeriodTestIdPrefix,
  disabled,
  inputAccessoryViewID,
}) => {
  const localeObj = useLocale();
  const locale = serializeLocaleToBCP47(localeObj);
  const gt = useI18n();
  const basePayLabel = gt.gettext("Minimum base pay");

  const { fieldsetError, inputError, selectError } = getPayErrorMessages({
    gt,
    value,
    showErrors,
    locale,
    payBounds,
    required,
  });

  const formattedValue = useMemo(
    // Group the integer part but keep the in-progress decimal; Intl.NumberFormat.format() would drop
    // a trailing separator/zero ("12." → "12"), making decimals impossible to type.
    () => (value?.amount ? formatPayInputForDisplay(value.amount, locale) : ""),
    [value?.amount, locale],
  );

  const handleAmountChange = useCallback(
    (text: string) => {
      // Strip locale group separators before validating; the displayed value carries them (e.g.
      // "5,000"), so validating the raw text would reject typing once a separator appears.
      const filteredAmount = filterPayInput(text, locale);
      if (isValidPayInput(filteredAmount, locale)) {
        onChange({
          amount: filteredAmount,
          salaryType: value?.salaryType,
          sentiment: value?.sentiment,
        });
      }
    },
    [onChange, value?.salaryType, value?.sentiment, locale],
  );

  // inputError (required/zero) and fieldsetError (range) are both amount errors and mutually
  // exclusive; consolidate them so the amount error is rendered once (below the input) and can be
  // associated with the input for accessibility.
  const amountError = inputError || fieldsetError || "";
  const amountErrorId = amountInputTestID
    ? `${amountInputTestID}-error`
    : "minimum-base-pay-amount-error";
  const { currencyCode } = getLocaleCurrency(locale);
  const currencyHint = gt.gettext("Enter minimum pay amount in {0}", [currencyCode]);
  // Native announces this hint on focus; web associates the error via aria-describedby below.
  const amountAccessibilityHint = amountError
    ? `${gt.gettext("Error: ")}${amountError}. ${currencyHint}`
    : currencyHint;

  const containerStyles = variant === "compact" ? styles.compactContainer : styles.defaultContainer;

  return (
    <Flex sx={containerStyles}>
      {showEmployerAlert && <EmployersPayInfoAlert />}
      <Flex sx={styles.updatedWrapper}>
        <Flex sx={{ marginBottom: 4 }}>
          <FormField
            errorText={amountError}
            errorMessageId={amountErrorId}
            helperText={description}
            showRequiredIndicator={required}
            invalid={!!amountError}
            label={basePayLabel}
          >
            <PayInput
              ref={amountInputRef}
              testID={amountInputTestID}
              value={formattedValue}
              onChangeText={handleAmountChange}
              onFocus={onAmountFocus}
              editable={!disabled}
              aria-required={required}
              aria-label={basePayLabel}
              aria-invalid={amountError ? true : undefined}
              aria-describedby={amountError ? amountErrorId : undefined}
              accessibilityHint={amountAccessibilityHint}
              inputAccessoryViewID={inputAccessoryViewID}
              invalid={!!amountError}
              clearAccessibilityLabel={gt.pgettext("Clear pay amount button", "Clear")}
            />
          </FormField>
        </Flex>
        <PayPeriodSelect
          invalid={!!selectError || !!fieldsetError}
          value={value}
          onChange={onChange}
          errorText={selectError}
          showRequiredIndicator={required}
          firstChipRef={payPeriodFirstChipRef}
          accessibilityHint={payPeriodAccessibilityHint}
          testIdPrefix={payPeriodTestIdPrefix}
          disabled={disabled}
        />
      </Flex>
    </Flex>
  );
};

export default MinimumPay;
